import React, { ChangeEvent, useState } from 'react';
import {
	Box,
	Button,
	Flex,
	Input,
	Modal,
	ModalBody,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Text,
	useDisclosure,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { PenIcon } from '../../../utils/icons/pen';
import { useUserEditorProvider } from '../../../providers/UserEditor/userEditorProvider';
import { useMoralisCloudFunction } from 'react-moralis';
import { useUserProvider } from '../../../providers/User/userProvider';
import { t } from 'i18next';
import { createStandaloneToast } from '@chakra-ui/react';
import { Color } from 'src/theme/color';

export const EditUserNick = ({
	nickname,
	isOwner,
	onNickChange,
}: {
	nickname: string;
	isOwner: boolean;
	onNickChange: (nick: string) => void;
}) => {
	const userEditorData = useUserEditorProvider();
	const { data: userData } = useUserProvider();

	const [editorContent, setEditorContent] = useState(userEditorData.nickname ?? userData.nickname);
	const [isLoading, handleIsLoading] = useState(false);

	const { isOpen, onOpen, onClose } = useDisclosure();
	const { fetch: isNicknameTaken } = useMoralisCloudFunction('nicknameTaken');

	const { toast } = createStandaloneToast();

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;

		setEditorContent(value);
	};

	const handleClose = async () => {
		handleIsLoading(true);
		const resultIsNicknameTaken = await isNicknameTaken({ params: { nickname: editorContent } });
		handleIsLoading(false);

		if (resultIsNicknameTaken) {
			toast({ status: 'error', title: t('user.nickTaken').toString() });

			userEditorData.setNickname(null);
			return;
		}

		userEditorData.setNickname(editorContent);
		onNickChange(editorContent);
		onClose();
	};

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
			>
				<ModalOverlay />
				<ModalContent
					bg={Color.BACKGROUND_LIGHT}
					border={`1px solid ${Color.BACKGROUND_TERTIARY}`}
					m="0 20px"
					color="black"
					alignSelf="center"
				>
					<ModalHeader
						display="flex"
						justifyContent="space-between"
						alignItems="center"
						color="white"
					>
						<Text
							fontWeight="600"
							fontSize="20px"
						>
							{t('user.editNick').toString()}
						</Text>
						<Flex
							cursor="pointer"
							justifyContent="center"
							alignItems="center"
							maxW="20px"
							maxH="20px"
							onClick={onClose}
							border="1px solid white"
							borderRadius="50%"
							p="5px"
						>
							<CloseIcon
								w="7px"
								h="7px"
							/>
						</Flex>
					</ModalHeader>

					<ModalBody
						mb="25px"
						display="flex"
						alignItems="flex-end"
						gridGap="25px"
						flexDirection="column"
					>
						<Input
							_focus={{ boxShadow: 'none' }}
							borderColor={Color.BACKGROUND_TERTIARY}
							color="white"
							disabled={isLoading}
							maxLength={24}
							value={editorContent}
							onChange={handleChange}
						/>
						<Box>
							<Button
								onClick={handleClose}
								isLoading={isLoading}
								disabled={editorContent.length === 0 || editorContent === nickname || isLoading}
							>
								{t('default.close').toString()}
							</Button>
						</Box>
					</ModalBody>
				</ModalContent>
			</Modal>
			<Box pos="relative">
				<Text
					textOverflow="ellipsis"
					maxW={{ base: 'calc(100% - 100px)', sm: '100%', md: '100%', hashup: '320px' }}
					whiteSpace="nowrap"
					overflow="hidden"
					display="block"
					fontSize={{ base: '18px', hashup: '25px' }}
					lineHeight={{ base: '22px', hashup: '34px' }}
					fontWeight="600"
				>
					{nickname}
				</Text>
				{isOwner && (
					<Flex
						onClick={onOpen}
						cursor="pointer"
						borderRadius="5px"
						justifyContent="center"
						alignItems="center"
						top="-22px"
						pos="absolute"
						border={`1px solid ${Color.GRAY_BORDER_LIGHT}`}
						borderColor="white"
						bg={Color.BACKGROUND}
						w={{ base: '18px', hashup: '22px' }}
						h={{ base: '18px', hashup: '22px' }}
					>
						<PenIcon
							width={{ base: '8px', hashup: '10px' }}
							stroke="white"
						/>
					</Flex>
				)}
			</Box>
		</>
	);
};
